import {
  appShortName,
  hueForRank,
  HIDDEN_APP_NAME,
} from './activity-timeline.format';

import type {
  ActivityInterval,
  ActivitySnapshot,
} from './activity-timeline.query';

export const UNKNOWN_APP = 'Unknown app';

export const UNKNOWN_DEVICE = 'no device';

const deviceIdOf = (snapshot: ActivitySnapshot) =>
  snapshot.deviceId ?? UNKNOWN_DEVICE;

const CAPTURE_GAP_THRESHOLD_MS = 5 * 60 * 1000;

/** How much time a derived interval covers when nothing follows its last snapshot. */
const SNAPSHOT_COVERAGE_MS = 60 * 1000;

export type Span = { startedAt: Date; endedAt: Date };

export type Violation = {
  kind:
    | 'overlapping_observation'
    | 'outside_observation'
    | 'snapshot_interval_mismatch';
  message: string;
  /**
   * What to select to look at it. A capture's `intervalId` is the interval it was placed in, so
   * that the violation also shows on that interval's detail.
   */
  target:
    | { kind: 'interval'; intervalId: string }
    | { kind: 'snapshot'; snapshotId: number; intervalId: string };
};

export type FocusCard = {
  key: string;
  interval: ActivityInterval;
  appName: string;
  /** The app name the captures recorded, which is what the ingestion view filters on */
  capturedAppName: string | null;
  appKey: string;
  /** The app's colour for the day, set once the day's apps are ranked */
  hue: number;
  label: string | null;
  hiddenBy: string | null;
  isDerived: boolean;
  isOpen: boolean;
  startedAt: Date;
  endedAt: Date;
  durationMs: number;
  presentSpans: Span[];
  presentMs: number;
  snapshots: ActivitySnapshot[];
  violations: Violation[];
};

export type ObservationSpan = {
  key: string;
  interval: ActivityInterval;
  startedAt: Date;
  endedAt: Date;
  isOpen: boolean;
  /**
   * The backend closed this observation because the device stopped sending heartbeats, so its end
   * can still change
   */
  heartbeatTimedOut: boolean;
  violations: Violation[];
};

/**
 * A stretch of the day with no observation, and its reason: why the observation before it ended,
 * or, when the day's data has none before it, why the one after it began.
 */
export type UnobservedSpan = {
  key: string;
  startedAt: Date;
  endedAt: Date;
  durationMs: number;
  reason: string;
};

export type LockBand = { key: string; startedAt: Date; endedAt: Date };

export type PresenceSpan = Span & {
  interval: ActivityInterval;
  violations: Violation[];
};

export type DeviceColumn = {
  deviceId: string;
  observation: ObservationSpan[];
  observedMs: number;
  unobserved: UnobservedSpan[];
  /** The presence intervals merged, for the present and attended totals */
  presentSpans: Span[];
  presence: PresenceSpan[];
  cards: FocusCard[];
  locks: LockBand[];
  orphans: ActivitySnapshot[];
  violations: Violation[];
};

export type AppTotals = {
  appKey: string;
  appName: string;
  hue: number;
  focusedMs: number;
  attendedMs: number;
};

export type ActivityDayShape = {
  columns: DeviceColumn[];
  now: Date;
  elapsedMs: number;
  observedMs: number;
  presentMs: number;
  attendedMs: number;
  unobservedMs: number;
  unobservedReasons: { reason: string; durationMs: number }[];
  apps: AppTotals[];
  snapshotCount: number;
  cardCount: number;
  violations: Violation[];
};

const clamp = (value: number, low: number, high: number) =>
  Math.min(Math.max(value, low), high);

export const mergedSpans = (spans: readonly Span[]): Span[] => {
  const merged: Span[] = [];
  for (const span of [...spans].sort(
    (one, other) => one.startedAt.getTime() - other.startedAt.getTime()
  )) {
    const last = merged[merged.length - 1];
    if (
      last !== undefined &&
      span.startedAt.getTime() <= last.endedAt.getTime()
    ) {
      if (span.endedAt.getTime() > last.endedAt.getTime())
        last.endedAt = span.endedAt;
    } else {
      merged.push({ startedAt: span.startedAt, endedAt: span.endedAt });
    }
  }
  return merged;
};

export const spansMs = (spans: readonly Span[]) =>
  spans.reduce(
    (total, span) => total + span.endedAt.getTime() - span.startedAt.getTime(),
    0
  );

const overlapOf = (one: Span, other: Span): Span | null => {
  const startedAt = Math.max(
    one.startedAt.getTime(),
    other.startedAt.getTime()
  );
  const endedAt = Math.min(one.endedAt.getTime(), other.endedAt.getTime());
  return endedAt > startedAt
    ? { startedAt: new Date(startedAt), endedAt: new Date(endedAt) }
    : null;
};

const overlapsOf = (one: Span, others: readonly Span[]): Span[] =>
  others.map((other) => overlapOf(one, other)).filter((span) => span !== null);

/** The stretches of `within` that no span covers. `spans` must be sorted by start. */
const gapsOf = (spans: readonly Span[], within: Span): Span[] => {
  const gaps: Span[] = [];
  let cursor = within.startedAt.getTime();
  for (const span of spans) {
    if (span.startedAt.getTime() > cursor) {
      gaps.push({
        startedAt: new Date(cursor),
        endedAt: new Date(
          Math.min(span.startedAt.getTime(), within.endedAt.getTime())
        ),
      });
    }
    cursor = Math.max(cursor, span.endedAt.getTime());
  }
  if (cursor < within.endedAt.getTime()) {
    gaps.push({ startedAt: new Date(cursor), endedAt: within.endedAt });
  }
  return gaps.filter((gap) => gap.endedAt.getTime() > gap.startedAt.getTime());
};

const drawnSpan = (interval: ActivityInterval, day: Span): Span => {
  const startedAt = new Date(interval.start).getTime();
  const endedAt =
    interval.end === null
      ? day.endedAt.getTime()
      : new Date(interval.end).getTime();
  return {
    startedAt: new Date(
      clamp(startedAt, day.startedAt.getTime(), day.endedAt.getTime())
    ),
    endedAt: new Date(
      clamp(endedAt, day.startedAt.getTime(), day.endedAt.getTime())
    ),
  };
};

/**
 * Focus intervals for devices that send none, built from runs of snapshots of one window. Runs are
 * tracked per device, so that captures from two devices that interleave in time do not cut each
 * other's cards.
 */
const derivedFocusIntervals = (
  snapshots: readonly ActivitySnapshot[]
): ActivityInterval[] => {
  const derived: ActivityInterval[] = [];
  const openPerDevice = new Map<
    string,
    { interval: ActivityInterval; app: string | null; lastAt: Date }
  >();

  for (const snapshot of snapshots) {
    const deviceId = deviceIdOf(snapshot);
    const capturedAt = new Date(snapshot.capturedAt);
    const open = openPerDevice.get(deviceId);
    const continues =
      open !== undefined &&
      open.app === snapshot.app &&
      open.interval.title === snapshot.title &&
      capturedAt.getTime() - open.lastAt.getTime() <= CAPTURE_GAP_THRESHOLD_MS;

    if (open !== undefined && continues) {
      open.lastAt = capturedAt;
      open.interval.end = new Date(
        capturedAt.getTime() + SNAPSHOT_COVERAGE_MS
      ).toISOString();
      continue;
    }

    if (
      open !== undefined &&
      capturedAt.getTime() - open.lastAt.getTime() <= CAPTURE_GAP_THRESHOLD_MS
    ) {
      open.interval.end = capturedAt.toISOString();
    }

    const interval: ActivityInterval = {
      id: `derived-${deviceId}-${capturedAt.getTime()}-${snapshot.id}`,
      type: 'focus',
      deviceId,
      start: capturedAt.toISOString(),
      end: new Date(capturedAt.getTime() + SNAPSHOT_COVERAGE_MS).toISOString(),
      openReason: 'derived',
      closeReason: 'derived',
      heartbeatAt: null,
      tzOffsetMinutes: null,
      capabilities: null,
      bundleIdentifier: null,
      title: snapshot.title,
      url: snapshot.url,
      hiddenBy: null,
    };
    derived.push(interval);
    openPerDevice.set(deviceId, {
      interval,
      app: snapshot.app,
      lastAt: capturedAt,
    });
  }

  return derived;
};

type TimedInterval = {
  interval: ActivityInterval;
  startMs: number;
  endMs: number | null;
};

const placeSnapshots = (
  snapshots: readonly ActivitySnapshot[],
  focusByDevice: Map<string, ActivityInterval[]>
) => {
  const snapshotsByInterval = new Map<string, ActivitySnapshot[]>();
  const orphansByDevice = new Map<string, ActivitySnapshot[]>();
  const violationsBySnapshot = new Map<number, Violation[]>();

  const timedByDevice = new Map(
    [...focusByDevice].map(([deviceId, intervals]) => [
      deviceId,
      intervals.map((interval): TimedInterval => ({
        interval,
        startMs: new Date(interval.start).getTime(),
        endMs: interval.end === null ? null : new Date(interval.end).getTime(),
      })),
    ])
  );
  const byIdByDevice = new Map(
    [...timedByDevice].map(([deviceId, timed]) => [
      deviceId,
      new Map(timed.map((entry) => [entry.interval.id, entry])),
    ])
  );
  const latestFirstByDevice = new Map(
    [...timedByDevice].map(([deviceId, timed]) => [
      deviceId,
      [...timed].reverse(),
    ])
  );
  // An open interval contains every capture after its start, without the end of the drawn day as a
  // bound, because that end can be earlier than the latest capture
  const holds =
    (capturedMs: number) =>
    ({ startMs, endMs }: TimedInterval) =>
      capturedMs >= startMs && (endMs === null || capturedMs <= endMs);

  for (const snapshot of snapshots) {
    const deviceId = deviceIdOf(snapshot);
    const capturedMs = new Date(snapshot.capturedAt).getTime();
    const taggedEntry =
      snapshot.focusIntervalId === null
        ? undefined
        : byIdByDevice.get(deviceId)?.get(snapshot.focusIntervalId);
    // A capture is tagged when its snapshot job is scheduled and timed later, so it can fall after
    // the end of its interval. Only a capture timed before the start of its interval contradicts
    // the tag
    if (taggedEntry !== undefined && capturedMs < taggedEntry.startMs) {
      violationsBySnapshot.set(snapshot.id, [
        {
          kind: 'snapshot_interval_mismatch',
          message: `capture ${snapshot.id} comes before its tagged interval starts`,
          target: {
            kind: 'snapshot',
            snapshotId: snapshot.id,
            intervalId: taggedEntry.interval.id,
          },
        },
      ]);
    }

    // `holds` is inclusive at both ends, so a capture on a shared boundary goes to the later interval
    const focusInterval =
      taggedEntry?.interval ??
      latestFirstByDevice.get(deviceId)?.find(holds(capturedMs))?.interval;
    if (focusInterval === undefined) {
      const orphans = orphansByDevice.get(deviceId) ?? [];
      orphans.push(snapshot);
      orphansByDevice.set(deviceId, orphans);
      continue;
    }
    const intervalSnapshots = snapshotsByInterval.get(focusInterval.id) ?? [];
    intervalSnapshots.push(snapshot);
    snapshotsByInterval.set(focusInterval.id, intervalSnapshots);
  }

  return { snapshotsByInterval, orphansByDevice, violationsBySnapshot };
};

/** The latest end time of the cards. An earlier card can end last when cards overlap in time. */
export const cardsEndedAt = (cards: readonly FocusCard[]) =>
  new Date(Math.max(...cards.map((card) => card.endedAt.getTime())));

const cardOf = (
  interval: ActivityInterval,
  span: Span,
  snapshots: ActivitySnapshot[],
  presentSpans: readonly Span[],
  violationsBySnapshot: ReadonlyMap<number, Violation[]>,
  appNameByBundleId: ReadonlyMap<string, string>,
  isDerived: boolean
): FocusCard => {
  const cardPresentSpans = overlapsOf(span, presentSpans);
  const violations: Violation[] = snapshots.flatMap(
    (snapshot) => violationsBySnapshot.get(snapshot.id) ?? []
  );

  const capturedAppName = isDerived
    ? (snapshots.find((snapshot) => snapshot.app !== null)?.app ?? null)
    : interval.bundleIdentifier === null
      ? null
      : (appNameByBundleId.get(interval.bundleIdentifier) ?? null);
  const appName =
    capturedAppName ??
    appShortName(interval.bundleIdentifier) ??
    (interval.hiddenBy === null ? UNKNOWN_APP : HIDDEN_APP_NAME);

  return {
    key: interval.id,
    interval,
    appName,
    capturedAppName,
    appKey: appName.toLowerCase(),
    hue: 0,
    label: interval.title ?? interval.url,
    hiddenBy: interval.hiddenBy,
    isDerived,
    isOpen: interval.end === null,
    startedAt: span.startedAt,
    endedAt: span.endedAt,
    durationMs: span.endedAt.getTime() - span.startedAt.getTime(),
    presentSpans: cardPresentSpans,
    presentMs: spansMs(cardPresentSpans),
    snapshots,
    violations,
  };
};

/**
 * The time of a lock comes from the focus close, because a presence interval closed by a lock ends
 * at its last input, which can be well before the lock.
 */
const lockBands = (
  intervals: readonly ActivityInterval[],
  day: Span
): LockBand[] => {
  const lockedAt = intervals
    .filter(
      (interval) =>
        interval.type === 'focus' &&
        interval.closeReason === 'lock' &&
        interval.end !== null
    )
    .map((interval) => new Date(interval.end as string).getTime());
  // If an unlock has no lock before it in its observation, the screen was locked when the day or
  // the observation began, so the band starts when the observation started
  const observations = intervals.filter(
    (interval) => interval.type === 'observation'
  );
  const unlocks = intervals.filter(
    (interval) => interval.type === 'focus' && interval.openReason === 'unlock'
  );
  const lockedSinceObservationStart = new Set<number>();
  for (const unlock of unlocks) {
    const unlockedMs = new Date(unlock.start).getTime();
    const observation = observations.find(
      (interval) =>
        new Date(interval.start).getTime() <= unlockedMs &&
        (interval.end === null ||
          new Date(interval.end).getTime() >= unlockedMs)
    );
    const lockedSinceMs =
      observation === undefined
        ? day.startedAt.getTime()
        : new Date(observation.start).getTime();
    const lockedBefore = lockedAt.some(
      (locked) => locked >= lockedSinceMs && locked < unlockedMs
    );
    if (!lockedBefore && lockedSinceMs < unlockedMs) {
      lockedAt.push(lockedSinceMs);
      lockedSinceObservationStart.add(lockedSinceMs);
    }
  }
  // A band also ends where its observation closed, because nothing is recorded about the lock after
  // that
  const unlockedAt = intervals
    .filter(
      (interval) =>
        interval.type === 'focus' &&
        (interval.openReason === 'unlock' || interval.openReason === 'wake')
    )
    .map((interval) => new Date(interval.start).getTime());
  const observationEndedAt = intervals
    .filter(
      (interval) => interval.type === 'observation' && interval.end !== null
    )
    .map((interval) => new Date(interval.end as string).getTime());

  const bands = lockedAt.map((startedAt) => {
    // A lock can close its observation at the same moment. A band from an observation's start does
    // not end where the observation before it closed at that moment
    const startsAtLock = !lockedSinceObservationStart.has(startedAt);
    const unlocked = [
      ...unlockedAt.filter((unlock) => unlock > startedAt),
      ...observationEndedAt.filter(
        (ended) => ended > startedAt || (startsAtLock && ended === startedAt)
      ),
    ];
    return {
      startedAt: new Date(startedAt),
      endedAt: new Date(
        unlocked.length === 0 ? day.endedAt.getTime() : Math.min(...unlocked)
      ),
    };
  });

  return mergedSpans(bands)
    .map((band) => overlapOf(band, day))
    .filter((band) => band !== null)
    .map((band) => ({
      key: `lock-${band.startedAt.getTime()}`,
      ...band,
    }));
};

const observationSpansOf = (
  intervals: readonly ActivityInterval[],
  day: Span
): ObservationSpan[] => {
  const spans = intervals.map((interval) => {
    const span = drawnSpan(interval, day);
    return {
      key: interval.id,
      interval,
      startedAt: span.startedAt,
      endedAt: span.endedAt,
      isOpen: interval.end === null,
      // If an observation timed out after the end of the drawn day, the observer was running for
      // all of its part in the day, so it is not marked as timed out
      heartbeatTimedOut:
        interval.closeReason === 'heartbeat_timeout' &&
        interval.end !== null &&
        new Date(interval.end).getTime() <= day.endedAt.getTime(),
      violations: [] as Violation[],
    };
  });

  for (const [index, span] of spans.entries()) {
    const next = spans[index + 1];
    if (
      next !== undefined &&
      next.startedAt.getTime() < span.endedAt.getTime()
    ) {
      span.violations.push({
        kind: 'overlapping_observation',
        message: `observation ${span.interval.id.slice(0, 8)} overlaps the next one on its device`,
        target: { kind: 'interval', intervalId: span.interval.id },
      });
    }
  }
  return spans;
};

const unobservedSpansOf = (
  observation: readonly ObservationSpan[],
  day: Span
): UnobservedSpan[] =>
  gapsOf(mergedSpans(observation), day).map((gap) => {
    const before = [...observation]
      .reverse()
      .find((span) => span.endedAt.getTime() <= gap.startedAt.getTime());
    const after = observation.find(
      (span) => span.startedAt.getTime() === gap.endedAt.getTime()
    );
    return {
      key: `unobserved-${gap.startedAt.getTime()}`,
      startedAt: gap.startedAt,
      endedAt: gap.endedAt,
      durationMs: gap.endedAt.getTime() - gap.startedAt.getTime(),
      reason:
        before?.interval.closeReason ??
        after?.interval.openReason ??
        'not started',
    };
  });

export const buildActivityDayShape = (
  snapshots: readonly ActivitySnapshot[],
  intervals: readonly ActivityInterval[],
  dayStart: Date,
  dayEnd: Date,
  now: Date
): ActivityDayShape => {
  const ceiling = new Date(Math.min(now.getTime(), dayEnd.getTime()));
  const day: Span = { startedAt: dayStart, endedAt: ceiling };
  const deviceIdsWithIntervals = new Set(
    intervals.map((interval) => interval.deviceId)
  );
  const derivedIntervals = derivedFocusIntervals(
    snapshots.filter(
      (snapshot) => !deviceIdsWithIntervals.has(deviceIdOf(snapshot))
    )
  );
  const drawnIntervals = [...intervals, ...derivedIntervals];

  const deviceIds = [
    ...new Set(drawnIntervals.map((interval) => interval.deviceId)),
  ].sort();

  const focusByDevice = new Map(
    deviceIds.map((deviceId) => [
      deviceId,
      drawnIntervals.filter(
        (interval) =>
          interval.type === 'focus' && interval.deviceId === deviceId
      ),
    ])
  );

  const { snapshotsByInterval, orphansByDevice, violationsBySnapshot } =
    placeSnapshots(snapshots, focusByDevice);

  // A capture has the app's display name, and an interval has only its bundle id, so each bundle
  // takes its name from a capture. Only a capture tagged with the interval counts, because a
  // capture placed by time can belong to the app the user just left.
  const appNameByBundleId = new Map<string, string>();
  for (const interval of drawnIntervals) {
    if (interval.bundleIdentifier === null) continue;
    const namingSnapshot = (snapshotsByInterval.get(interval.id) ?? []).find(
      (snapshot) =>
        snapshot.app !== null && snapshot.focusIntervalId === interval.id
    );
    if (
      namingSnapshot !== undefined &&
      namingSnapshot.app !== null &&
      !appNameByBundleId.has(interval.bundleIdentifier)
    ) {
      appNameByBundleId.set(interval.bundleIdentifier, namingSnapshot.app);
    }
  }

  const columns = deviceIds.map((deviceId): DeviceColumn => {
    const onDevice = drawnIntervals.filter(
      (interval) => interval.deviceId === deviceId
    );
    const presentSpans = mergedSpans(
      onDevice
        .filter((interval) => interval.type === 'presence')
        .map((interval) => drawnSpan(interval, day))
    );
    const observation = observationSpansOf(
      onDevice.filter((interval) => interval.type === 'observation'),
      day
    );
    const observed = mergedSpans(observation);
    // Each observation is checked on its own, because the merged union would join observations that
    // touch and hide a row that the observer failed to split at their boundary
    const runsOutsideObservation = (span: Span) =>
      !observation.some(
        (obs) =>
          obs.startedAt.getTime() <= span.startedAt.getTime() &&
          span.endedAt.getTime() <= obs.endedAt.getTime()
      );

    const cards = (focusByDevice.get(deviceId) ?? []).map((interval) =>
      cardOf(
        interval,
        drawnSpan(interval, day),
        snapshotsByInterval.get(interval.id) ?? [],
        presentSpans,
        violationsBySnapshot,
        appNameByBundleId,
        !deviceIdsWithIntervals.has(deviceId)
      )
    );
    if (deviceIdsWithIntervals.has(deviceId)) {
      for (const card of cards) {
        if (runsOutsideObservation(card)) {
          card.violations.push({
            kind: 'outside_observation',
            message: `focus ${card.interval.id.slice(0, 8)} runs outside every observation on its device`,
            target: { kind: 'interval', intervalId: card.interval.id },
          });
        }
      }
    }
    const presence = onDevice
      .filter((interval) => interval.type === 'presence')
      .map((interval): PresenceSpan => {
        const span = drawnSpan(interval, day);
        return {
          ...span,
          interval,
          violations: runsOutsideObservation(span)
            ? [
                {
                  kind: 'outside_observation',
                  message: `presence ${interval.id.slice(0, 8)} runs outside every observation on its device`,
                  target: { kind: 'interval', intervalId: interval.id },
                },
              ]
            : [],
        };
      });

    const orphans = orphansByDevice.get(deviceId) ?? [];

    return {
      deviceId,
      observation,
      observedMs: spansMs(observed),
      unobserved: deviceIdsWithIntervals.has(deviceId)
        ? unobservedSpansOf(observation, day)
        : [],
      presentSpans,
      presence,
      cards,
      locks: lockBands(onDevice, day),
      orphans,
      violations: [
        ...observation.flatMap((span) => span.violations),
        ...presence.flatMap((span) => span.violations),
        ...orphans.flatMap(
          (snapshot) => violationsBySnapshot.get(snapshot.id) ?? []
        ),
      ],
    };
  });

  const observedSpans = mergedSpans(
    columns.flatMap((column) => column.observation)
  );
  const presentSpans = mergedSpans(
    columns.flatMap((column) => column.presentSpans)
  );
  const attendedSpans = mergedSpans(
    columns.flatMap((column) =>
      column.cards.flatMap((card) => card.presentSpans)
    )
  );
  // Gaps in the union of every device's observation, each ascribed to the observation whose end
  // starts it, so the breakdown adds up to the header's unobserved time
  const unobserved = unobservedSpansOf(
    columns
      .flatMap((column) => column.observation)
      .sort((one, other) => one.endedAt.getTime() - other.endedAt.getTime()),
    day
  );

  // Each app's spans are merged before they are summed, as the day totals are, so focus
  // intervals of one app that overlap across devices count once
  const allCards = columns.flatMap((column) => column.cards);
  const cardsByApp = new Map<string, FocusCard[]>();
  for (const card of allCards) {
    const appCards = cardsByApp.get(card.appKey) ?? [];
    appCards.push(card);
    cardsByApp.set(card.appKey, appCards);
  }
  const apps = [...cardsByApp]
    .map(([appKey, cards]) => ({
      appKey,
      cards,
      focusedMs: spansMs(mergedSpans(cards)),
      attendedMs: spansMs(
        mergedSpans(cards.flatMap((card) => card.presentSpans))
      ),
    }))
    .sort((one, other) => other.focusedMs - one.focusedMs)
    .map(
      ({ appKey, cards, focusedMs, attendedMs }, rank, ranked): AppTotals => {
        const hue = hueForRank(rank, ranked.length);
        for (const card of cards) card.hue = hue;
        return {
          appKey,
          appName: cards[0].appName,
          hue,
          focusedMs,
          attendedMs,
        };
      }
    );
  const unobservedByReason = new Map<string, number>();
  for (const span of unobserved) {
    unobservedByReason.set(
      span.reason,
      (unobservedByReason.get(span.reason) ?? 0) + span.durationMs
    );
  }

  return {
    columns,
    now,
    elapsedMs: ceiling.getTime() - dayStart.getTime(),
    observedMs: spansMs(observedSpans),
    presentMs: spansMs(presentSpans),
    attendedMs: spansMs(attendedSpans),
    unobservedMs: spansMs(unobserved),
    unobservedReasons: [...unobservedByReason]
      .map(([reason, durationMs]) => ({ reason, durationMs }))
      .sort((one, other) => other.durationMs - one.durationMs),
    apps,
    snapshotCount: snapshots.length,
    cardCount: allCards.length,
    violations: [
      ...columns.flatMap((column) => column.violations),
      ...allCards.flatMap((card) => card.violations),
    ],
  };
};
